import { InAppState } from "../base/InAppState";
import { User } from "../../models/User";
import template from './AccountSettingsTemplate.html';

export class AccountSettingsState extends InAppState
{
    static ROUTE = '/account';
    static ID = 'account-settings';

    async enter()
    {
        this._render();
    }

    _render( activeTabSelector = null )
    {
        this.app.view.render(
            this.app.view.pageContent,
            template,
            { user: this.app.user }
        );

        this._bindEventListeners();

        if ( activeTabSelector )
        {
            this.switchTab( activeTabSelector );
        }
    }

    _bindEventListeners()
    {
        // Tab switching
        this.app.view.pageContent.querySelectorAll( '[data-type="account-tab"]' ).forEach( ( tab ) => {
            this.addEventListener( tab, 'click', () => {
                this.switchTab( tab.getAttribute( 'data-tab-target' ) );
            });
        });

        // Edit profile button
        const editProfileBtn = this.app.view.pageContent.querySelector( '[data-type="edit-profile"]' );
        if ( editProfileBtn )
        {
            this.addEventListener( editProfileBtn, 'click', () => this._showModal( 'editProfileModal' ) );
        }

        this._bindModalButtons( 'profile', 'editProfileModal', () => this._saveProfile() );

        // Change email button
        const changeEmailBtn = this.app.view.pageContent.querySelector( '[data-type="change-email"]' );
        if ( changeEmailBtn )
        {
            this.addEventListener( changeEmailBtn, 'click', () => this._showModal( 'changeEmailModal' ) );
        }

        this._bindModalButtons( 'email', 'changeEmailModal', () => this._saveEmailChange() );

        // Password live validation
        const currentPasswordInput = this.app.view.pageContent.querySelector( '#current-password' );
        if ( currentPasswordInput )
        {
            this.addEventListener( currentPasswordInput, 'input', () => this._clearCurrentPasswordError() );
        }

        const newPasswordInput = this.app.view.pageContent.querySelector( '#new-password' );
        const confirmPasswordInput = this.app.view.pageContent.querySelector( '#confirm-password' );

        if ( newPasswordInput )
        {
            this.addEventListener( newPasswordInput, 'input', () => this._onNewPasswordInput() );
        }

        if ( confirmPasswordInput )
        {
            this.addEventListener( confirmPasswordInput, 'input', () => this._onConfirmPasswordInput() );
        }

        // Change password button
        const savePasswordBtn = this.app.view.pageContent.querySelector( '[data-type="save-password"]' );
        if ( savePasswordBtn )
        {
            this.addEventListener( savePasswordBtn, 'click', () => this._savePassword() );
        }
    }

    _bindModalButtons( prefix, modalId, saveCallback )
    {
        const closeBtn = this.app.view.pageContent.querySelector( `[data-type="${prefix}-modal-close"]` );
        const cancelBtn = this.app.view.pageContent.querySelector( `[data-type="${prefix}-modal-cancel"]` );
        const saveBtn = this.app.view.pageContent.querySelector( `[data-type="${prefix}-modal-save"]` );

        if ( closeBtn )
        {
            this.addEventListener( closeBtn, 'click', () => this._hideModal( modalId ) );
        }

        if ( cancelBtn )
        {
            this.addEventListener( cancelBtn, 'click', () => this._hideModal( modalId ) );
        }

        if ( saveBtn )
        {
            this.addEventListener( saveBtn, 'click', saveCallback );
        }
    }

    _showModal( modalId )
    {
        const modal = this.app.view.pageContent.querySelector( `#${modalId}` );
        if ( modal )
        {
            modal.classList.add( 'show' );
            modal.style.display = 'block';
            document.body.classList.add( 'modal-open' );

            const backdrop = document.createElement( 'div' );
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = `${modalId}Backdrop`;
            document.body.appendChild( backdrop );
        }
    }

    _hideModal( modalId )
    {
        const modal = this.app.view.pageContent.querySelector( `#${modalId}` );
        const backdrop = document.getElementById( `${modalId}Backdrop` );

        if ( modal )
        {
            modal.classList.remove( 'show' );
            modal.style.display = 'none';
            document.body.classList.remove( 'modal-open' );
        }

        if ( backdrop )
        {
            backdrop.remove();
        }
    }

    async _saveProfile()
    {
        const form = this.app.view.pageContent.querySelector( '#edit-profile-form' );
        const nameInput = form?.querySelector( '#edit-profile-name' );

        if ( !nameInput?.value?.trim() )
        {
            this.app.view.toast( 'Name is required', 'error' );
            return;
        }

        const modal = this.app.view.pageContent.querySelector( '#editProfileModal' );
        this.app.view.blockUI( modal.querySelector( '.modal-content' ), 'Saving profile...' );

        try
        {
            const { result } = await this.app.api.patch( '/user/data', {
                name: nameInput.value.trim()
            });

            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );

            if ( result.status === 200 && result.data )
            {
                this.app.user = new User( result.data );
                this._hideModal( 'editProfileModal' );
                this._render();
                document.querySelector( 'header-bar' )?.render();
                this.app.view.toast( 'Profile updated successfully', 'success' );
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to update profile', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );
            this.app.view.toast( error?.message || 'An error occurred while saving profile', 'error' );
        }
    }

    async _saveEmailChange()
    {
        const form = this.app.view.pageContent.querySelector( '#change-email-form' );
        const emailInput = form?.querySelector( '#change-email-new' );
        const passwordInput = form?.querySelector( '#change-email-password' );

        if ( !emailInput?.value?.trim() )
        {
            this.app.view.toast( 'New email is required', 'error' );
            return;
        }

        if ( !passwordInput?.value )
        {
            this.app.view.toast( 'Password is required', 'error' );
            return;
        }

        const modal = this.app.view.pageContent.querySelector( '#changeEmailModal' );
        this.app.view.blockUI( modal.querySelector( '.modal-content' ), 'Sending confirmation...' );

        try
        {
            const { result } = await this.app.api.post( '/user/email-change', {
                newEmail: emailInput.value.trim(),
                password: passwordInput.value
            });

            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );

            if ( result.status === 200 )
            {
                form.reset();
                this._hideModal( 'changeEmailModal' );
                this.app.view.toast( result.data?.message || 'Confirmation email sent. Please check your new email.', 'success' );
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to initiate email change', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( modal.querySelector( '.modal-content' ) );

            if ( error?.status === 403 )
            {
                passwordInput.classList.add( 'is-invalid' );
                let feedback = form.querySelector( '#change-email-password-error' );
                if ( !feedback )
                {
                    feedback = document.createElement( 'div' );
                    feedback.id = 'change-email-password-error';
                    feedback.className = 'invalid-feedback';
                    passwordInput.parentElement.appendChild( feedback );
                }
                feedback.textContent = 'Invalid password';
                feedback.style.display = 'block';
                passwordInput.value = '';
                passwordInput.focus();
            }
            else if ( error?.status === 409 )
            {
                emailInput.classList.add( 'is-invalid' );
                let feedback = form.querySelector( '#change-email-new-error' );
                if ( !feedback )
                {
                    feedback = document.createElement( 'div' );
                    feedback.id = 'change-email-new-error';
                    feedback.className = 'invalid-feedback';
                    emailInput.parentElement.appendChild( feedback );
                }
                feedback.textContent = 'This email address is already in use';
                feedback.style.display = 'block';
            }
            else
            {
                this.app.view.toast( error?.message || 'An error occurred', 'error' );
            }
        }
    }

    _setCurrentPasswordError( message )
    {
        const container = this.app.view.pageContent;
        const input = container.querySelector( '#current-password' );
        if ( input )
        {
            input.classList.add( 'is-invalid' );
            input.value = '';
            input.focus();
        }

        let feedback = container.querySelector( '#current-password-error' );
        if ( !feedback )
        {
            feedback = document.createElement( 'div' );
            feedback.id = 'current-password-error';
            feedback.className = 'invalid-feedback';
            input.parentElement.appendChild( feedback );
        }
        feedback.textContent = message;
        feedback.style.display = 'block';
    }

    _clearCurrentPasswordError()
    {
        const container = this.app.view.pageContent;
        const input = container.querySelector( '#current-password' );
        if ( input )
        {
            input.classList.remove( 'is-invalid' );
        }

        const feedback = container.querySelector( '#current-password-error' );
        if ( feedback )
        {
            feedback.style.display = 'none';
        }
    }

    _getPasswordStrength( password )
    {
        let score = 0;
        if ( password.length >= 6 ) score++;
        if ( password.length >= 10 ) score++;
        if ( /[a-z]/.test( password ) && /[A-Z]/.test( password ) ) score++;
        if ( /\d/.test( password ) ) score++;
        if ( /[^a-zA-Z0-9]/.test( password ) ) score++;

        if ( score <= 1 ) return { level: 'weak', width: '25%', color: '#fb977d', text: 'Weak' };
        if ( score <= 2 ) return { level: 'fair', width: '50%', color: '#f8c076', text: 'Fair' };
        if ( score <= 3 ) return { level: 'good', width: '75%', color: '#5ab5e0', text: 'Good' };
        return { level: 'strong', width: '100%', color: '#4bd08b', text: 'Strong' };
    }

    _onNewPasswordInput()
    {
        const container = this.app.view.pageContent;
        const password = container.querySelector( '#new-password' )?.value || '';
        const meter = container.querySelector( '#password-strength-meter' );
        const bar = container.querySelector( '#password-strength-bar' );
        const text = container.querySelector( '#password-strength-text' );
        const lengthError = container.querySelector( '#password-length-error' );

        if ( password.length === 0 )
        {
            meter.style.display = 'none';
            lengthError.style.display = 'none';
        }
        else
        {
            meter.style.display = 'block';
            const strength = this._getPasswordStrength( password );
            bar.style.width = strength.width;
            bar.style.backgroundColor = strength.color;
            text.textContent = strength.text;
            text.style.color = strength.color;

            lengthError.style.display = password.length < 6 ? 'block' : 'none';
        }

        this._onConfirmPasswordInput();
    }

    _onConfirmPasswordInput()
    {
        const container = this.app.view.pageContent;
        const newPassword = container.querySelector( '#new-password' )?.value || '';
        const confirmPassword = container.querySelector( '#confirm-password' )?.value || '';
        const matchError = container.querySelector( '#password-match-error' );

        if ( confirmPassword.length === 0 )
        {
            matchError.style.display = 'none';
        }
        else
        {
            matchError.style.display = newPassword !== confirmPassword ? 'block' : 'none';
        }
    }

    async _savePassword()
    {
        const currentPassword = this.app.view.pageContent.querySelector( '#current-password' )?.value;
        const newPassword = this.app.view.pageContent.querySelector( '#new-password' )?.value;
        const confirmPassword = this.app.view.pageContent.querySelector( '#confirm-password' )?.value;

        if ( !currentPassword )
        {
            this.app.view.toast( 'Current password is required', 'error' );
            return;
        }

        if ( !newPassword || newPassword.length < 6 )
        {
            this.app.view.toast( 'Password must be at least 6 characters', 'error' );
            return;
        }

        if ( newPassword !== confirmPassword )
        {
            this.app.view.toast( 'Passwords do not match', 'error' );
            return;
        }

        const pageContent = this.app.view.pageContent;
        this.app.view.blockUI( pageContent, 'Updating password...' );

        try
        {
            const { result } = await this.app.api.patch( '/auth/user/password', {
                currentPassword,
                newPassword
            });

            this.app.view.unblockUI( pageContent );

            if ( result.status === 200 )
            {
                this._render( '#pills-security' );
                this.app.view.toast( 'Password updated successfully', 'success' );
            }
            else
            {
                this.app.view.toast( result.data?.message || 'Failed to update password', 'error' );
            }
        }
        catch ( error )
        {
            this.app.view.unblockUI( pageContent );

            if ( error?.status === 403 )
            {
                this._setCurrentPasswordError( 'Invalid current password' );
            }
            else
            {
                this.app.view.toast( error?.message || 'An error occurred while updating password', 'error' );
            }
        }
    }

    switchTab( targetSelector )
    {
        const container = this.app.view.pageContent;

        container.querySelectorAll( '[data-type="account-tab"]' ).forEach( ( tab ) => {
            tab.classList.remove( 'active' );
            tab.setAttribute( 'aria-selected', 'false' );
        });

        container.querySelectorAll( '.tab-pane' ).forEach( ( pane ) => {
            pane.classList.remove( 'show', 'active' );
        });

        const activeTab = container.querySelector( `[data-type="account-tab"][data-tab-target="${targetSelector}"]` );
        if ( activeTab )
        {
            activeTab.classList.add( 'active' );
            activeTab.setAttribute( 'aria-selected', 'true' );
        }

        const activePane = container.querySelector( targetSelector );
        if ( activePane )
        {
            activePane.classList.add( 'show', 'active' );
        }
    }

    async exit()
    {
    }

    getContentHeaderData()
    {
        return {
            title: 'Account Settings',
            breadcrumb: [ 'Account', 'Settings' ]
        };
    }
}
